import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import { query } from '../../../../graphql'
import EmptyState from './EmptyState'

const ACTIVITY_ICONS = {
  CAREER_HISTORY_EVENT: '📋',
  SPACE_HISTORY_EVENT: '🎯',
  ASSESSMENT_COMPLETED: '✅',
  TALENT_INVITED: '👤',
  MATCHING_COMPLETED: '🔄',
  DEFAULT: '📝'
}

const getActivityIcon = (eventName) => {
  return ACTIVITY_ICONS[eventName] || ACTIVITY_ICONS.DEFAULT
}

const getActivityLabel = (eventName, t) => {
  const labelMap = {
    CAREER_HISTORY_EVENT: t('discover.activity.career_event'),
    SPACE_HISTORY_EVENT: t('discover.activity.space_event'),
    ASSESSMENT_COMPLETED: t('discover.activity.assessment_completed'),
    TALENT_INVITED: t('discover.activity.talent_invited'),
    MATCHING_COMPLETED: t('discover.activity.matching_completed')
  }
  return labelMap[eventName] || eventName
}

const formatActivityTime = (createdAt) => {
  const date = new Date(createdAt)
  const now = new Date()
  const diffInMinutes = Math.floor((now - date) / (1000 * 60))

  if (diffInMinutes < 1) return 'gerade eben'
  if (diffInMinutes < 60) return `vor ${diffInMinutes}m`
  if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60)
    return `vor ${hours}h`
  }

  return format(date, 'd. MMM HH:mm', { locale: de })
}

export default function ActivityLogs({ spaceId, limit = 10 }) {
  const { t } = useTranslation()

  const { data: logsData, isLoading } = useQuery({
    queryKey: QUERY_KEYS.activityLogs(spaceId, limit),
    queryFn: () =>
      query({
        query: 'logsBySpace',
        variables: {
          space_id: spaceId,
          sortDirection: 'DESC',
          limit
        }
      }),
    enabled: !!spaceId,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  const logs = logsData?.logsBySpace?.items || []

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent' />
      </div>
    )
  }

  if (logs.length === 0) {
    return (
      <EmptyState
        title={t('discover.activity.no_activity')}
        description={t('discover.activity.no_activity_description')}
      />
    )
  }

  return (
    <div className='flex flex-col gap-y-3'>
      <h3 className='px-2 text-sm font-semibold text-gray-700'>
        {t('discover.activity.recent_activity')}
      </h3>
      <div className='flex flex-col gap-y-2'>
        {logs.map((log, index) => {
          let activityData = {}
          try {
            activityData = JSON.parse(log.data || '{}')
          } catch (e) {
            // Ignore parse errors
          }

          return (
            <div
              key={log.id || index}
              className='flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 transition-colors hover:bg-gray-100'
            >
              <div className='mt-1 text-lg'>
                {getActivityIcon(log.event_name)}
              </div>
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-medium text-gray-900'>
                  {getActivityLabel(log.event_name, t)}
                </p>
                {activityData.message && (
                  <p className='mt-1 line-clamp-2 text-xs text-gray-600'>
                    {activityData.message}
                  </p>
                )}
                <p className='mt-1 text-xs text-gray-500'>
                  {formatActivityTime(log.createdAt)}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

ActivityLogs.propTypes = {
  spaceId: PropTypes.string.isRequired,
  limit: PropTypes.number
}
